import { IAppContext } from 'App/AppContext';
import { IGraphInterface } from 'App/AppContextProvider';
import { t } from 'i18next';
import ResourceLink from 'models/resourceLink';
import { ResourceListType } from 'models/resourceList';
import { HomeAccount } from 'models/userSetting';
import { apiUpdateLink } from 'services/Api/linkService';
import { apiUpdateList } from 'services/Api/listService';
import { apiRequest, GraphScopeId } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import {
  graphGetDocumentLibraryItemThumbnails,
  graphGetDriveItem,
  graphGetDriveItemForUrl,
  graphGetDriveItemPreview,
  graphGetDriveItemUrl,
  graphGetIDriveItemVersions,
  graphGetListItem,
  graphGetListItemVersions,
  graphGetSite,
  graphGetSitePage,
  graphGetSitePageList,
  graphGetSitePageListItem,
  graphPublishDriveItem,
  graphPublishSitePage,
  graphValidateDriveItem,
  graphValidateListItem,
  graphValidateSitePageItem,
} from 'services/Graph/graphService';
import { ISite, ISitePage } from 'services/Graph/SharepointInterfaces';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';

/**
 * SharePoint content operations for a library item, called from the browser with the user's own
 * delegated Graph token. They used to live on the ResourceLink model itself; moving them behind
 * linkContentService is what lets a category on another provider answer the same questions
 * through the API broker instead.
 *
 * Virtual categories keep arriving here too: the short-circuits inside each function are the
 * behaviour they had before, so nothing about them changed.
 */

export async function getThumbnailSharePoint(link: ResourceLink, appContext: IAppContext): Promise<string> {
  if (!link.list.spDriveId || !link.driveItemId) return '';

  try {
    const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, link.list.altTenantId);
    const thumbUrl = await graphGetDocumentLibraryItemThumbnails(
      graphInterface.client,
      link.list.spDriveId,
      link.driveItemId,
    );

    return thumbUrl ?? '';
  } catch (err) {
    logger.debug('Error while getting the thumbnail url', err);

    return '';
  }
}

export async function publishSharePoint(link: ResourceLink, appContext: IAppContext): Promise<void> {
  try {
    switch (link.list.listType) {
      case ResourceListType.SitePageLibrary:
        if (!link.list.spSiteId || !link.pageId) {
          throw new AppError('spSiteId or pageId is empty');
        }
        const graphInterfacePage = await appContext.getGraphInterface(GraphScopeId.Library, link.list.altTenantId);
        await graphPublishSitePage(graphInterfacePage.client, link.list.spSiteId, link.pageId);
        setMetaDataSharePoint(link, graphInterfacePage, undefined, appContext);
        break;
      case ResourceListType.DocumentLibrary:
        if (!link.list.spDriveId || !link.driveItemId) {
          throw new AppError('spDriveId or driveItemId is empty');
        }
        const graphInterfaceDoc = await appContext.getGraphInterface(GraphScopeId.Library, link.list.altTenantId);
        await graphPublishDriveItem(graphInterfaceDoc.client, link.list.spDriveId, link.driveItemId);
        setMetaDataSharePoint(link, undefined, graphInterfaceDoc, appContext);
        break;
      default:
        throw new AppError('List type not supported for publishing');
    }
  } catch (err) {
    logger.debug('publish', err);

    const appErr = err as AppError;
    if (appErr.code === 'notAllowed') {
      appContext.showNotification(t('sharepoint:Errors.ResourceLocked'), true);
    } else {
      appContext.showNotification(t('sharepoint:Errors.AccessDenied'), true);
    }

    return;
  }
}

export async function setMetaDataSharePoint(
  link: ResourceLink,
  graphInterfacePages: IGraphInterface | undefined,
  graphInterfaceDocuments: IGraphInterface | undefined,
  appContext: IAppContext,
): Promise<void> {
  //
  // Get all versions and last modification date of the item
  //
  try {
    if (link.isWholeList()) {
      return;
    }

    if (link.list && link.list.enableMetaData === true && !link.list.isVirtual) {
      switch (link.list.listType) {
        case ResourceListType.DocumentLibrary:
          if (!graphInterfaceDocuments) return;
          if (!link.list.spDriveId || !link.driveItemId) {
            logger.debug('Error while getting the meta data: drive id or drive item id is empty', link.list);

            return;
          }

          const versions = await graphGetIDriveItemVersions(
            graphInterfaceDocuments.client,
            link.list.spDriveId,
            link.driveItemId,
          );

          link.applyMetaDataBatchResultDriveItem(versions);
          break;
        case ResourceListType.SitePageLibrary:
          if (!graphInterfacePages) return;
          if (!link.list.spSiteId || !link.pageId) {
            logger.debug('Error while getting the meta data: site id or page id is empty', link.list);

            return;
          }

          //try to fix spListId
          if (!link.list.spListId) {
            const pageList = await graphGetSitePageList(graphInterfacePages.client, link.list.spSiteId);
            link.list.spListId = pageList?.id;
            if (link.list.spListId && hasUserFeatureGenericManager(appContext)) {
              const accessToken = await appContext.getAccessToken(apiRequest.scopes);
              await apiUpdateList(accessToken, link.list);
              logger.debug('spListId updated', link.list);
            }
          }

          //try to fix listItemId
          if (!link.listItemId && link.list.spListId) {
            const listItem = await graphGetSitePageListItem(
              graphInterfacePages.client,
              link.list.spSiteId,
              link.list.spListId,
              link.linkURL,
            );
            if (listItem?.id) {
              link.listItemId = listItem.id;
              const accessToken = await appContext.getAccessToken(apiRequest.scopes);
              await apiUpdateLink(link, false, accessToken, appContext.globalDataCache);
              logger.debug('listItemId updated', link);
            }
          }

          if (link.listItemId && link.list.spListId) {
            //get the complete version history
            const versions = await graphGetListItemVersions(
              graphInterfacePages.client,
              link.list.spSiteId,
              link.list.spListId,
              link.listItemId,
            );

            link.applyMetaDataBatchResultListItem(versions);
          }

          //there should be at least 1 version
          //when this fails, try to get the latest version from the page
          if (!link.versions || link.versions.length === 0) {
            //we cannot get the list item. fill the version history, only from the page model
            const page = await graphGetSitePage(graphInterfacePages.client, link.list.spSiteId, link.pageId, false);
            link.versionInfo.publishingState = ResourceLink.getPublishingStateFromSitePage(page?.publishingState.level);
            if (page?.publishingState?.versionId) {
              link.versionInfo.id = page.publishingState.versionId;
              link.versionInfo.date = page.lastModifiedDateTime ? new Date(page.lastModifiedDateTime) : new Date();
              link.versionInfo.version = page.publishingState.versionId;
            }
          }
          break;
        default:
          break;
      }
    }
  } catch (err) {
    logger.debug('Error while getting the meta data', err);
  }
}

export async function getEmbedURLSharePoint(link: ResourceLink, appContext: IAppContext): Promise<string | undefined> {
  logger.debug('getEmbedURL', link);

  if (link.isWholeList()) {
    //return the web URL of the list
    if (link.list) {
      return link.list.webURL || '';
    }
  }

  let url: string | undefined = undefined;

  try {
    if (link.list) {
      switch (link.list.listType) {
        case ResourceListType.DocumentLibrary:
          if (link.driveItemId && link.list.spDriveId) {
            const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, link.list.altTenantId);

            let driveId: string = link.list.spDriveId;
            let driveItemId: string = link.driveItemId;

            const result = await graphGetDriveItemUrl(appContext, driveId, driveItemId, link.linkURL);
            if (result?.isUrlFile && result.url) {
              if (result.urlClassification?.embedable) {
                const item = await graphGetDriveItemForUrl(graphInterface.client, result.url);
                driveId = item?.parentReference?.driveId ?? '';
                driveItemId = item?.id ?? '';
              } else {
                driveId = '';
                driveItemId = '';
              }
            }

            if (driveId && driveItemId) {
              const embedUrl = await graphGetDriveItemPreview(graphInterface.client, driveId, driveItemId);

              if (embedUrl) {
                url = embedUrl;
              }
            }
          }
          break;
        default:
          break;
      }
    }
  } catch (err) {
    logger.debug('Error while getting the embed url', err);
  } finally {
    if (url && link.linkURLFragment && link.linkURLFragment.trim() !== '') {
      url += link.linkURLFragment;
    }
  }

  return url;
}

export async function getWebURLSharePoint(link: ResourceLink, appContext: IAppContext): Promise<string> {
  //
  // Get the current web URL of the link from SharePoint
  // - when available, also update the versionInfo
  // - support for .url files
  //
  logger.debug('getWebURL', link);

  if (link.isWholeList() && link.list) {
    //return the web URL of the list
    return link.list.webURL || '';
  }

  let url: string = link.linkURL;
  let spUrl: string = '';
  let dontUpdateLinkUrl = false;

  try {
    if (link.list) {
      switch (link.list.listType) {
        case ResourceListType.DocumentLibrary:
          if (link.driveItemId && link.list.spDriveId) {
            const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, link.list.altTenantId);
            const item = await graphGetDriveItem(graphInterface.client, link.list.spDriveId, link.driveItemId);
            if (item && item.webUrl) {
              //try to resolve .url files
              const result = await graphGetDriveItemUrl(appContext, link.list.spDriveId, link.driveItemId, item.webUrl);
              if (result?.url) {
                spUrl = result.url;
                if (result.isUrlFile) {
                  //don't update the link URL with a .url file
                  dontUpdateLinkUrl = true;
                }
              }
              //update metadata
              link.setMetaDataForDriveItem(item);
            }
          }
          break;
        case ResourceListType.CustomList:
          if (link.listItemId && link.list.spSiteId && link.list.spListId) {
            const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, link.list.altTenantId);
            const item = await graphGetListItem(
              graphInterface.client,
              link.list.spSiteId,
              link.list.spListId,
              link.listItemId,
            );
            const listItemUrl = link.buildLinkURLFromListItem(item);
            if (listItemUrl) {
              spUrl = listItemUrl;
            }
            //no metadata to update
          }
          break;
        case ResourceListType.SitePageLibrary:
          if (link.pageId && link.list.spSiteId) {
            const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, link.list.altTenantId);

            const page: ISitePage = await graphGetSitePage(graphInterface.client, link.list.spSiteId, link.pageId, false);

            spUrl = page.webUrl;
            if (!spUrl.startsWith('https://')) {
              const site: ISite = await graphGetSite(graphInterface.client, link.list.spSiteId);
              spUrl = site.webUrl + '/' + url;
            }

            //update metadata
            link.setMetaDataForPage(page);
          }
          break;
        case ResourceListType.WebURL:
        //nothing to-do
      }
    }
  } catch (err) {
    logger.debug('Error while getting the web url', err);
  } finally {
    if (!dontUpdateLinkUrl && spUrl && spUrl !== url && link.list && link.list.listType !== ResourceListType.WebURL) {
      try {
        //update the link with the new URL from SharePoint
        link.linkURL = spUrl;
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        await apiUpdateLink(link, true, accessToken, appContext.globalDataCache);
      } catch (err) {
        logger.debug('Error update link after getting web url', err);
      }
    }
    if (spUrl) {
      url = spUrl;
    }
    if (link.linkURLFragment && link.linkURLFragment.trim() !== '') {
      url += link.linkURLFragment;
    }
  }

  return url;
}

export async function isValidSharePoint(link: ResourceLink, appContext: IAppContext): Promise<boolean | undefined> {
  link.isValidated = true;
  if (link.isWholeList()) return true;
  if (!link.list) return false;
  if (link.list.isVirtual) return true;

  if (link.list.altTenantId) {
    //don't validate links in other tenants except the home account of the current user
    const currentHomeAccount = appContext.globalDataCache.userSettings.get(HomeAccount) as string;
    if (!currentHomeAccount || link.list.altTenantId !== currentHomeAccount) {
      return undefined;
    }
  }

  switch (link.list.listType) {
    case ResourceListType.DocumentLibrary:
      if (link.driveItemId && link.list.spDriveId) {
        const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, link.list.altTenantId);

        return graphValidateDriveItem(graphInterface.client, link.list.spDriveId, link.driveItemId);
      } else {
        return false;
      }

    case ResourceListType.CustomList:
      if (link.listItemId && link.list.spSiteId && link.list.spListId) {
        const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, link.list.altTenantId);

        return graphValidateListItem(graphInterface.client, link.list.spSiteId, link.list.spListId, link.listItemId);
      } else {
        return false;
      }

    case ResourceListType.SitePageLibrary:
      if (link.pageId && link.list.spSiteId) {
        const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, link.list.altTenantId);

        return graphValidateSitePageItem(graphInterface.client, link.list.spSiteId, link.pageId);
      } else {
        return false;
      }

    case ResourceListType.WebURL:
      return fetch(link.linkURL, { mode: 'no-cors' })
        .then(() => {
          return true;
        })
        .catch(() => {
          return false;
        });

    default:
      return undefined;
  }
}
